import { STALE_TIME } from "@/constants";
import {
	getTypeofHiring,
	jobJustificationFinanceAction,
} from "@/services/hiring-justification-functions";
import { ApprovalStatus } from "@/services/invoice-functions";
import { JobJustificationFinanceActionParams } from "@/types/hiring-justification.types";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FaCheck, FaXmark } from "react-icons/fa6";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "../ui/button";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "../ui/dialog";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../ui/form";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { Textarea } from "../ui/textarea";

const formSchema = z.object({
	note: z.string().min(1, {
		message: "Notes are required",
	}),
	type_of_hiring: z.number().optional(),
});

const FinanceJobApproveOrReject: React.FC<{
	jobId: number;
	accessToken: string;
}> = ({ jobId, accessToken }) => {
	const [open, setOpen] = useState(false);
	const [approvalStatus, setApprovalStatus] =
		useState<ApprovalStatus>("Approved");

	const queryClient = useQueryClient();
	const navigate = useNavigate();

	const typesOfHiring = useQuery({
		queryKey: ["types-of-hiring"],
		queryFn: () => getTypeofHiring(accessToken),
		staleTime: STALE_TIME,
	});

	const actionJobJustification = useMutation({
		mutationKey: ["job", "finance-action", jobId],
		mutationFn: (data: JobJustificationFinanceActionParams) =>
			jobJustificationFinanceAction(accessToken, data),
		onSuccess: () => {
			queryClient.invalidateQueries(["job"]);
			toast.success("Job Justification Actioned", {
				description: `Job ${jobId} has successfully been submitted`,
			});
			setOpen(false);
			form.reset();
			actionJobJustification.reset();
			navigate("/finance-job-approvals");
		},
		onError: () => {
			toast.error("Error submitting remark");
		},
	});

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			note: "",
		},
	});

	const onSubmit = (data: z.infer<typeof formSchema>) => {
		if (approvalStatus === "Approved") {
			if (!data.type_of_hiring) {
				form.setError("type_of_hiring", {
					type: "manual",
					message: "This is required when approving",
				});
				return;
			}
			actionJobJustification.mutate({
				finance_partner_approval_note: data.note,
				jobid: jobId,
				type_of_hiring: data.type_of_hiring,
			});
			return;
		}
		actionJobJustification.mutate({
			finance_partner_rejected_note: data.note,
			jobid: jobId,
		});
	};

	const disabled = () => {
		if (actionJobJustification.isLoading) return true;
		if (actionJobJustification.isSuccess) return true;
		return false;
	};

	useEffect(() => {
		form.reset();
	}, [open]);

	return (
		<Dialog open={open} onOpenChange={(open) => setOpen(open)}>
			<div className="flex gap-2">
				<DialogTrigger asChild>
					<Button onClick={() => setApprovalStatus("Approved")}>Approve</Button>
				</DialogTrigger>
				<DialogTrigger asChild>
					<Button onClick={() => setApprovalStatus("Rejected")} variant="secondary">
						Disapprove
					</Button>
				</DialogTrigger>
			</div>
			<DialogContent className="sm:max-w-md">
				<DialogHeader className="text-left">
					{approvalStatus === "Approved" ? (
						<>
							<DialogTitle className="text-green-500 flex gap-2 items-center mb-1">
								<div className="bg-green-200 p-1 rounded-full">
									<FaCheck className="text-xl" />
								</div>
								Approving Job Justification
							</DialogTitle>
							<DialogDescription>Add your notes here.</DialogDescription>
						</>
					) : (
						<>
							<DialogTitle className="text-red-500 flex gap-2 items-center mb-1">
								<div className="bg-red-200 p-1 rounded-full">
									<FaXmark className="text-xl" />
								</div>
								Disapproving Job Justification
							</DialogTitle>
							<DialogDescription>Add your notes here.</DialogDescription>
						</>
					)}
				</DialogHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)}>
						<div className="flex items-center space-x-2 mb-4">
							<div className="grid flex-1 gap-2">
								{approvalStatus === "Approved" ? (
									typesOfHiring.isLoading ? (
										<span>Loading Users</span>
									) : typesOfHiring.data ? (
										<FormField
											name="type_of_hiring"
											render={({ field }) => (
												<FormItem className="mb-2">
													<FormLabel>Type of Hiring</FormLabel>
													<FormControl>
														<Select
															onValueChange={(e) => {
																field.onChange(parseFloat(e));
															}}
														>
															<SelectTrigger>
																<SelectValue placeholder="Select an option" />
															</SelectTrigger>
															<SelectContent>
																{typesOfHiring.data.map((item) => (
																	<SelectItem value={item.type_of_hiring_id.toString()}>
																		{item.type_hiring_desc}
																	</SelectItem>
																))}
															</SelectContent>
														</Select>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
									) : null
								) : null}
								<FormField
									name="note"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Notes</FormLabel>
											<FormControl>
												<Textarea {...field} disabled={disabled()} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</div>

						<DialogFooter className="sm:justify-end">
							<DialogClose asChild>
								<Button type="button" variant="ghost">
									Cancel
								</Button>
							</DialogClose>
							<Button type="submit" disabled={disabled()}>
								{disabled() ? "Submitting..." : "Submit"}
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
};

export default FinanceJobApproveOrReject;
